import { LoiteringCamera } from '@hakimo-ui/hakimo/types';
import { Label } from '@hakimo-ui/shared/ui-base';
import { VideoCameraIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { PersonStanding } from 'lucide-react';
import ClickableTimestamp from './ClickableTimestamp';

dayjs.extend(utc);
dayjs.extend(timezone);

interface LoiteringDetailsProps {
  cameras: LoiteringCamera[];
  onTimestampClick: (timestamp: string) => void;
  timeZone: string;
}

function LoiteringDetails({
  cameras,
  onTimestampClick,
  timeZone,
}: LoiteringDetailsProps) {
  if (!cameras?.length) return null;
  const formatTime = (timestamp: string) =>
    dayjs.utc(timestamp).tz(timeZone).format('HH:mm:ss');
  return (
    <div className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
      {cameras.map((camera) => (
        <div className="space-y-3">
          {camera.entities.map((entity) => (
            <div className="grid grid-cols-[24px_auto] gap-x-1">
              <div className="row-span-4 flex pt-0.5">
                <PersonStanding className="h-6 w-6" />
              </div>

              <p>
                <ClickableTimestamp
                  text={entity.description}
                  onTimestampClick={onTimestampClick}
                />
              </p>

              <div className="grid grid-cols-2">
                <div className="col-span-2">
                  Triggered at:{' '}
                  <span className="text-ondark-primary">
                    {entity.triggered_at_utc ? (
                      <ClickableTimestamp
                        text={formatTime(entity.triggered_at_utc)}
                        onTimestampClick={onTimestampClick}
                      />
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
                <span>
                  First seen:{' '}
                  <span className="text-ondark-primary">
                    <ClickableTimestamp
                      text={formatTime(entity.first_seen)}
                      onTimestampClick={onTimestampClick}
                    />
                  </span>
                </span>

                <span>
                  Last seen:{' '}
                  <span className="text-ondark-primary">
                    <ClickableTimestamp
                      text={formatTime(entity.last_seen)}
                      onTimestampClick={onTimestampClick}
                    />
                  </span>
                </span>

                <span>Dwell: {entity.dwell_minutes} mins</span>
                <span>Appearance: {entity.appearance_minutes} mins</span>
              </div>

              <div className="flex items-center gap-2">
                <VideoCameraIcon className="h-4 w-4" />
                <Label small text={camera.camera_name} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default LoiteringDetails;
